import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Button, Input, Select } from '../components/ui/Forms';
import { Modal } from '../components/ui/Modal';
import SafeIcon from '../common/SafeIcon';
import { formatDate } from '../utils/dateUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function Receipts() {
  const { state, addReceipt, deleteReceipt } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ subscriptionId: '', date: '', amount: '', notes: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    addReceipt(formData);
    setIsModalOpen(false);
    setFormData({ subscriptionId: '', date: '', amount: '', notes: '' });
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text('SubSift Receipt History', 14, 22);
    
    const tableData = state.receipts.map(r => {
      const sub = state.subscriptions.find(s => s.id === r.subscriptionId);
      return [
        formatDate(r.date),
        sub ? sub.name : 'Unknown',
        `$${r.amount}`,
        r.notes || '-'
      ];
    });

    doc.autoTable({
      startY: 30,
      head: [['Date', 'Subscription', 'Amount', 'Notes']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 10 },
      headStyles: { fillColor: [13, 148, 136] } // Teal-600
    });

    doc.save('subsift-receipts.pdf');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Receipts</h1>
          <p className="text-slate-500">Log and export your payment history.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={generatePDF} disabled={state.receipts.length === 0}>
            <SafeIcon name="Download" /> Export PDF
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>
            <SafeIcon name="Plus" /> Log Payment
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 text-sm">
              <th className="p-4 font-medium">Date</th>
              <th className="p-4 font-medium">Subscription</th>
              <th className="p-4 font-medium">Amount</th>
              <th className="p-4 font-medium">Notes</th>
              <th className="p-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {state.receipts.sort((a,b) => new Date(b.date) - new Date(a.date)).map(receipt => {
              const sub = state.subscriptions.find(s => s.id === receipt.subscriptionId);
              return (
                <tr key={receipt.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="p-4 text-slate-700">{formatDate(receipt.date)}</td>
                  <td className="p-4 font-medium text-slate-800">{sub ? sub.name : 'Deleted Sub'}</td>
                  <td className="p-4 text-slate-700">${receipt.amount}</td>
                  <td className="p-4 text-slate-500 text-sm max-w-xs truncate">{receipt.notes}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => deleteReceipt(receipt.id)} className="text-slate-400 hover:text-red-500">
                      <SafeIcon name="Trash2" className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {state.receipts.length === 0 && (
              <tr>
                <td colSpan="5" className="p-8 text-center text-slate-500">
                  No receipts logged yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Log Payment Receipt">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Subscription"
            value={formData.subscriptionId}
            onChange={e => setFormData({...formData, subscriptionId: e.target.value})}
            options={state.subscriptions.map(s => ({ label: s.name, value: s.id }))}
            required
          />
          <Input type="date" label="Payment Date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
          <Input type="number" step="0.01" label="Amount Paid ($)" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} required />
          <Input label="Notes (Optional)" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
          
          <div className="pt-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit">Save Receipt</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}